import i18next from 'i18next'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { Link } from 'widgets-for-react'
import { ButtonPrimary } from '../../../../utils/buttons'
import { blockInviewHandler } from '../../../../utils/inview'
import { convertReactNodeToString, slugify } from '../../../../utils/utils'
import { PictureLayout } from '../../image/imageLayout'
import { RootLayout } from '../../rootLayout'
import { getKnowledgeSlug } from '../knowledgeUtils'
import { KnowledgeHighlight } from './knowledgeHighlightState'

type KnowledgeHighlightProps = {
  teaser: KnowledgeHighlight | 'empty' | 'error' | 'loading'
}

export const KnowledgeHighlightLayout = ({
  teaser,
}: KnowledgeHighlightProps) => (
  <InView
    key={`io-component-knowledgeHighlight-${slugify(
      convertReactNodeToString(
        typeof teaser === 'string' ? teaser : teaser.title
      )
    )}`}
    as="section"
    triggerOnce={true}
    onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
    className="block block--knowledge-highlight"
  >
    {teaser === 'loading' ? (
      <RootLayout.Loader hasWrapper={true} showText={true} />
    ) : teaser === 'error' ? (
      <RootLayout.LoadingFailed
        text={i18next.t('common:loadingFailed', {
          entity: i18next.t('knowledge:knowledgeTitle').toLowerCase(),
        })}
      />
    ) : teaser === 'empty' ? (
      <RootLayout.EmptyState
        text={i18next.t('common:emptyState', {
          entity: i18next.t('knowledge:knowledgeTitle').toLowerCase(),
        })}
      />
    ) : (
      <KnowledgeHighlightInner teaser={teaser} />
    )}
  </InView>
)

type KnowledgeHighlightInnerProps = { teaser: KnowledgeHighlight }

export const KnowledgeHighlightInner = ({
  teaser,
}: KnowledgeHighlightInnerProps) => {
  const { title, introduction, image, slug, type } = teaser
  const linkLabel = i18next.t('home:knowledgeArticleCTA')
  return (
    <div className="block__inner container--m gutter--m">
      <div className="block__wrapper">
        <header className="block__header reverse-order">
          <h2 className="block__title title--m">{title}</h2>
          <span className="block__label title--xxs text--brand">
            {i18next.t('home:knowledgeArticleTitle')}
          </span>
        </header>
        <div className="block__content">
          <p className="block__intro">{introduction}</p>
          <Link
            to={getKnowledgeSlug(type, slug)}
            className="block__visual"
            aria-label={linkLabel}
            title={linkLabel}
          >
            <PictureLayout.Img
              image={image}
              width="620"
              height="380"
              extraClasses="block__image"
              lazyload
            />
          </Link>
          <div className="block__clickable">
            <ButtonPrimary
              kind="link"
              size="s"
              extraClasses="block__button"
              to={getKnowledgeSlug(type, slug)}
              label={linkLabel}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
